import { UHttp } from '../../utils/UHttp';
import companyStore from "../../store/company-store";

// Types
export type BookingStatus = 'confirmed' | 'cancelled' | 'completed';
export type PaymentStatus = 'pending' | 'paid' | 'partially_paid' | 'refunded';

export type DateRangeMode = 'day' | 'week' | 'month';

export interface DateRangeFilter {
  startDate: string; // Format ISO: YYYY-MM-DD
  endDate: string;   // Format ISO: YYYY-MM-DD
  mode: DateRangeMode;
}

export interface BookingOption {
  id: string;
  name: string;
  price: number;
  categoryId: string;
}

export interface BookingService {
  id?: string;
  serviceId: string;
  serviceName: string;
  orderIndex: number;
  optionIds?: string[];
  price: number;
  duration: number;
  status?: 'active' | 'cancelled';
  cancelledAt?: string | null;
  refundAmount?: number | null;
  options?: BookingOption[];
}

export interface Appointment {
  id: string;
  time: string;
  clientName: string;
  service: string;
  duration: string;
  status: 'confirmed' | 'cancelled' | 'completed';
  totalPrice: number
}

export interface BookingResponse {
  success: boolean;
  message: string;
  status:number;
  data?: BookingDetail | BookingListItem[] | null;
}

export interface BookingListItemPublic {
  id: string;
  serviceName: string;
  startTime: string;
  endTime: string;
  status: BookingStatus;
  totalPrice: number;
  firstName: string;
  lastName: string;
  businessName?: string;
  options?: Array<{
    id: string;
    name: string;
    price: number;
  }>;
}

export interface BookingListItem {
  id: string;
  status: BookingStatus;
  startTime: string;
  endTime: string;
  timeSlotId: string;
  // Legacy single service fields (for backward compatibility)
  serviceName?: string;
  serviceId?: string;
  serviceDuration?: number;
  servicePrice?: number;
  // Multi-services fields
  services?: BookingService[];
  totalDuration?: number;
  clientName: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone?: string;
  totalPrice: number;
  paidAmount?: number; // finalAmount - montant après réduction
  totalChargedToClient?: number; // Montant total payé par le client (avec frais de service 2%)
  paymentType?: 'full' | 'deposit';
  paymentStatus?: PaymentStatus;
  netAmount?: number; // Montant net reçu par le professionnel (après frais de service)
  refundAmount?: number; // Montant remboursé en cas d'annulation
  remainingBalance?: number; // Solde restant à encaisser (pour les services avec acompte)
  options?: BookingOption[];
  createdAt?: string; // Date de création de la réservation (utile pour les réservations sans créneau)
  isManualBooking?: boolean; // Indique si la réservation a été créée manuellement par le professionnel
}

export interface BookingDetail {
  id: string;
  status: BookingStatus;
  totalPrice: number;
  paidAmount?: number; // finalAmount - montant après réduction
  totalChargedToClient?: number; // Montant total payé par le client (avec frais de service 2%)
  paymentType?: 'full' | 'deposit';
  paymentStatus?: PaymentStatus;
  netAmount?: number; // Montant net reçu par le professionnel (après frais de service)
  refundAmount?: number; // Montant remboursé en cas d'annulation
  remainingBalance?: number; // Solde restant à encaisser (pour les services avec acompte)
  startTime: string;
  endTime: string;

  // Propriétés du modèle original
  timeSlot?: {
    id: string;
    startTime: string;
    endTime: string;
  };
  service?: {
    id: string;
    name: string;
    duration?: number;
    price: number;
  };
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };

  // Propriétés du nouveau modèle (format plat) - Legacy single service
  timeSlotId?: string;
  serviceName?: string;
  serviceId?: string;
  serviceDuration?: number;
  servicePrice?: number;
  clientName?: string;
  clientFirstName?: string;
  clientLastName?: string;
  clientEmail?: string;
  clientPhone?: string;

  // Multi-services fields
  services?: BookingService[];
  totalDuration?: number;

  options: BookingOption[];
  cancellationToken?: string;
  cancellationReason?: string;
  isManualBooking?: boolean; // Indique si la réservation a été créée manuellement par le professionnel
}

export interface BookingCreateParams {
  // Legacy single service format (deprecated)
  serviceId?: string;
  optionIds?: string[];
  // Multi-services format
  services?: Array<{
    serviceId: string;
    optionIds?: string[];
    price: number;
    duration: number;
  }>;
  timeSlotId?: string; // Optionnel pour les services sans réservation
  startTime?: string; // Optionnel pour les services sans réservation
  endTime?: string;   // Optionnel pour les services sans réservation
  clientId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  notes?: string;     // Pour les instructions spéciales ou informations additionnelles
  companyId?: string;
  collaboratorCompagnieId?: string; // Pour les réservations manuelles par un professionnel
  promoCode?: string; // Code promo à appliquer
}


const apiUrlServer = process.env.REACT_APP_URL_SERVER;

export class BookingModel {
  private static instance: BookingModel;

  private constructor() {}

  public static getInstance(): BookingModel {
    if (!BookingModel.instance) {
      BookingModel.instance = new BookingModel();
    }
    return BookingModel.instance;
  }

  /**
   * Récupère toutes les réservations du professionnel
   * @param period - Période de temps (jour, semaine, mois)
   * @param status - Statut des réservations à récupérer
   * @param companyId -
   * @param collaboratorCompagnieId -
   */
  public async getProfessionalBookings(
    dateFilter: DateRangeFilter,
    status: 'confirmed' | 'cancelled' | 'completed' | 'all',
    companyId: string,
    collaboratorCompagnieId?: string
  ): Promise<BookingResponse> {
    try {
      let url = `${apiUrlServer}/api/booking/collaborator/bookings`;

      // Commencer avec un point d'interrogation pour le premier paramètre
      url += `?startDate=${dateFilter.startDate}`;
      url += `&endDate=${dateFilter.endDate}`;

      // Ajouter le statut
      url += `&status=${status}`;

      // Ajouter l'ID de la compagnie si fourni
      if (companyId) {
        url += `&companyId=${companyId}`;
      }

      if (collaboratorCompagnieId) {
        url += `&collaboratorCompagnieId=${collaboratorCompagnieId}`;
      }

      const response = await UHttp.get(
        url,
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      console.error('Erreur lors de la récupération des réservations:', error);
      throw error;
    }
  }

  public async getProfessionalBookingsByDate(startDate: string, endDate: string, collaboratorCompagnieId?: string): Promise<BookingResponse> {
    try {

      let url = `${apiUrlServer}/api/booking/collaborator/bookings/by-date?startDate=${startDate}&endDate=${endDate}&companyId=${companyStore.getIdCompanySelectedDefault}`;

      // Ajouter le collaboratorCompagnieId s'il est fourni
      if (collaboratorCompagnieId) {
        url += `&collaboratorCompagnieId=${collaboratorCompagnieId}`;
      }

      const response = await UHttp.get(
          url,
          UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      console.error('Erreur lors de la récupération des réservations:', error);
      throw error;
    }
  }

  /**
   * Récupère une réservation par son ID
   */
  public async getBookingById(id: string): Promise<BookingResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/booking/${id}`,
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse

    } catch (error) {
      throw error;
    }
  }

  /**
   * Crée une nouvelle réservation
   */
  public async createBooking(params: BookingCreateParams): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/booking/collaborator`,
        params,
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Crée une réservation manuelle (par le professionnel)
   * Permet de créer des réservations en dehors des créneaux disponibles
   */
  public async createManualBooking(params: BookingCreateParams): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/api/booking/manual`,
        params,
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Met à jour une réservation manuelle
   */
  public async updateManualBooking(id: string, params: BookingCreateParams): Promise<BookingResponse> {
    try {
      const response = await UHttp.put(
        `${apiUrlServer}/api/booking/manual/${id}`,
        params,
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Met à jour le statut d'une réservation
   */
  public async updateBookingStatus(id: string, status: BookingStatus): Promise<BookingResponse> {
    try {
      const response = await UHttp.patch(
        `${apiUrlServer}/api/booking/status/${id}`,
        { status },
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * terminé une réservation par son id
   */
  public async completedBooking(id: string): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
          `${apiUrlServer}/api/booking/completed/${id}`,
          {},
          UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Annule une réservation par son id
   */
  public async cancelBooking(id: string): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
          `${apiUrlServer}/api/booking/cancel/${id}`,
          {},
          UHttp.authHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Exporte le chiffre d'affaires de l'entreprise ou d'un collaborateur
   * @param startDate - Date de début (YYYY-MM-DD)
   * @param endDate - Date de fin (YYYY-MM-DD)
   * @param companyId - ID de l'entreprise
   * @param format - Format d'export ('csv' ou 'pdf')
   * @param collaboratorCompagnieId - ID de la relation collaborateur-compagnie (optionnel)
   * @returns Le fichier exporté (téléchargement automatique)
   */
  public async exportCompanyRevenue(
    startDate: string,
    endDate: string,
    companyId: string,
    format: 'csv' | 'pdf',
    collaboratorCompagnieId?: string
  ): Promise<void> {
    try {
      // Construire l'URL avec les paramètres
      let url = `${apiUrlServer}/api/booking/export/company-revenue`;
      url += `?startDate=${startDate}`;
      url += `&endDate=${endDate}`;
      url += `&companyId=${companyId}`;
      url += `&format=${format}`;

      if (collaboratorCompagnieId) {
        url += `&collaboratorCompagnieId=${collaboratorCompagnieId}`;
      }

      // Faire la requête
      const response = await fetch(url, {
        method: 'GET',
        headers: UHttp.authHeaders()
      });

      // Vérifier si la réponse est OK
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'export');
      }

      // Récupérer le blob (fichier)
      const blob = await response.blob();

      // Créer un lien de téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Définir le nom du fichier
      const filename = `revenue_export_${startDate}_to_${endDate}.${format}`;
      link.setAttribute('download', filename);

      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();

      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur lors de l\'export du chiffre d\'affaires:', error);
      throw error;
    }
  }
}
